import { useCallback, useEffect, useState, useRef } from 'react'
import Cropper from 'react-easy-crop'
import {
  X, Upload, Loader2, AlertTriangle, Save, ImageIcon, ZoomIn, ZoomOut, RotateCcw,
  Sparkles, Undo2,
} from 'lucide-react'

/**
 * LogoEditor — drop-in square logo cropper.
 *
 *   Props:
 *     companyName?    used for the modal title
 *     initialSrc?     existing logo URL — if present, opens straight to crop
 *     onClose()
 *     onSave(dataUri) called with a 500×500 PNG `data:image/png;base64,…`
 *
 *   Behavior:
 *     - File picker accepts image/jpeg + image/png only
 *     - Cropper enforces a 1:1 (square) aspect
 *     - On save, the cropped region is drawn to a 500×500 canvas and exported
 *       as PNG (preserves transparency; predictable size ≤ ~250 KB)
 */
const OUT_SIZE = 500   // saved logo dimensions (px)
const MAX_DECODED_PX = 8192   // refuse absurdly large source images (memory safety)
const ACCEPT = 'image/jpeg,image/png,.jpg,.jpeg,.png'

// Zoom range. Going below 1 lets the user create padding around a non-square
// logo (the empty area becomes transparent in the saved PNG).
const ZOOM_MIN = 0.3
const ZOOM_MAX = 5

export default function LogoEditor({ companyName, initialSrc, onClose, onSave }) {
  const [src, setSrc] = useState(initialSrc || null)
  const [originalSrc, setOriginalSrc] = useState(initialSrc || null)  // preserved for "ใช้ภาพต้นฉบับ"
  const [bgRemoved, setBgRemoved] = useState(false)
  const [bgRemoving, setBgRemoving] = useState(false)
  const [bgErr, setBgErr] = useState(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedArea, setCroppedArea] = useState(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const fileRef = useRef(null)

  // If the parent passes a fresh initialSrc, reset crop state.
  useEffect(() => {
    if (initialSrc) {
      setSrc(initialSrc); setOriginalSrc(initialSrc)
      setCrop({ x: 0, y: 0 }); setZoom(1); setBgRemoved(false); setBgErr(null)
    }
  }, [initialSrc])

  function pickFile() { fileRef.current?.click() }

  /** Shared file ingestion: file picker, drag-drop, paste all funnel here. */
  function processFile(file) {
    if (!file) return
    setErr(null); setBgErr(null)
    if (!/^image\/(jpeg|png)$/.test(file.type)) { setErr('รองรับเฉพาะไฟล์ JPG หรือ PNG'); return }
    if (file.size > 10 * 1024 * 1024)         { setErr('ไฟล์ใหญ่เกิน 10 MB'); return }
    const reader = new FileReader()
    reader.onload = () => {
      const dataUri = String(reader.result || '')
      const img = new Image()
      img.onload = () => {
        if (img.naturalWidth > MAX_DECODED_PX || img.naturalHeight > MAX_DECODED_PX) {
          setErr('ภาพมีขนาดใหญ่เกินไป กรุณาย่อก่อนอัปโหลด'); return
        }
        setSrc(dataUri); setOriginalSrc(dataUri)
        setCrop({ x: 0, y: 0 }); setZoom(1); setBgRemoved(false)
      }
      img.onerror = () => setErr('โหลดภาพไม่สำเร็จ')
      img.src = dataUri
    }
    reader.onerror = () => setErr('อ่านไฟล์ไม่สำเร็จ')
    reader.readAsDataURL(file)
  }

  /** Remove the background using @imgly/background-removal (~30 MB lazy chunk).
   *  Keeps the original around so the user can revert if the result is bad. */
  async function removeBg() {
    if (!src || bgRemoving) return
    setBgRemoving(true); setBgErr(null)
    try {
      const { removeBackground } = await import('@imgly/background-removal')
      const resultBlob = await removeBackground(src)
      const url = URL.createObjectURL(resultBlob)
      // Convert to data URI so it survives canvas export cleanly.
      const reader = new FileReader()
      reader.onload = () => {
        setSrc(String(reader.result || url))
        setBgRemoved(true)
        URL.revokeObjectURL(url)
      }
      reader.readAsDataURL(resultBlob)
    } catch (e) {
      setBgErr(e?.message || 'ลบพื้นหลังไม่สำเร็จ')
    } finally {
      setBgRemoving(false)
    }
  }
  function revertBg() {
    if (!originalSrc) return
    setSrc(originalSrc); setBgRemoved(false); setBgErr(null)
  }

  function onFileChange(e) {
    processFile(e.target.files?.[0])
    // Allow re-picking the same file later (input doesn't re-fire change otherwise).
    if (e.target) e.target.value = ''
  }

  // ── Drag and drop: accept exactly one image file, ignore the rest.
  function onDragEnter(e) { e.preventDefault(); if (saving) return; setDragActive(true) }
  function onDragOver(e)  { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy' }
  function onDragLeave(e) {
    // Only clear when the drag leaves the container (not when it crosses a child).
    if (e.currentTarget.contains(e.relatedTarget)) return
    setDragActive(false)
  }
  function onDrop(e) {
    e.preventDefault()
    setDragActive(false)
    if (saving) return
    // Pick the FIRST file only — multi-drop is silently ignored except for [0].
    const file = e.dataTransfer?.files?.[0]
    processFile(file)
  }

  const onCropComplete = useCallback((_a, areaPx) => setCroppedArea(areaPx), [])

  async function save() {
    if (saving || !src || !croppedArea) return
    setSaving(true); setErr(null)
    try {
      const dataUri = await renderCropped(src, croppedArea, OUT_SIZE)
      await onSave(dataUri)
    } catch (e) {
      setErr(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-4">
      <div className="absolute inset-0 bg-zinc-900/55 backdrop-blur-[2px]" onClick={() => !saving && onClose()} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[1100px] max-h-[94vh] flex flex-col">
        <header className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-amber-700/80 font-semibold">Company logo</div>
            <h2 className="text-[16px] font-bold text-zinc-900 truncate" title={companyName || ''}>
              {companyName || 'อัปโหลดโลโก้บริษัท'}
            </h2>
          </div>
          <button onClick={onClose} disabled={saving} className="w-8 h-8 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 inline-flex items-center justify-center disabled:opacity-50">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_320px] min-h-0">
          {/* Crop stage — 1:1 panel with checkerboard so transparent padding is visible.
              Drag-and-drop accepts a single image; multiple files → only the first. */}
          <section
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className="relative aspect-square max-h-full w-full mx-auto"
            style={{
              backgroundColor: '#f5f5f4',
              backgroundImage: 'linear-gradient(45deg, #e5e5e4 25%, transparent 25%), linear-gradient(-45deg, #e5e5e4 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e5e4 75%), linear-gradient(-45deg, transparent 75%, #e5e5e4 75%)',
              backgroundSize: '20px 20px',
              backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0',
            }}
          >
            {src ? (
              <Cropper
                image={src}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid
                restrictPosition={false}    /* allow image to be smaller / off-center → transparent padding */
                objectFit="contain"
                minZoom={ZOOM_MIN}
                maxZoom={ZOOM_MAX}
                zoomSpeed={0.2}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                style={{ containerStyle: { backgroundColor: 'transparent' } }}
              />
            ) : (
              <button
                type="button"
                onClick={pickFile}
                className={'absolute inset-4 rounded-2xl border-2 border-dashed transition-colors flex flex-col items-center justify-center gap-2 ' +
                  (dragActive
                    ? 'border-amber-500 bg-amber-50/80 text-amber-700'
                    : 'border-zinc-300 bg-white text-zinc-500 hover:border-amber-400 hover:bg-amber-50/40 hover:text-amber-700')}
              >
                <ImageIcon size={28} strokeWidth={1.5} />
                <span className="text-[13.5px] font-medium">
                  {dragActive ? 'ปล่อยเพื่ออัปโหลด' : 'คลิกหรือลากไฟล์มาวาง'}
                </span>
                <span className="text-[11.5px]">JPG หรือ PNG · ไม่เกิน 10 MB · 1 ไฟล์</span>
              </button>
            )}
            {/* Drop overlay on top of the cropper when an image is already loaded —
                only shows during an active drag so it doesn't block crop interactions. */}
            {src && dragActive && (
              <div className="absolute inset-3 rounded-2xl border-2 border-dashed border-amber-500 bg-amber-50/85 text-amber-800 flex flex-col items-center justify-center gap-1.5 pointer-events-none">
                <ImageIcon size={22} />
                <span className="text-[13px] font-semibold">ปล่อยเพื่อแทนที่ภาพ</span>
                <span className="text-[11px]">รับเพียง 1 ไฟล์</span>
              </div>
            )}
          </section>

          {/* Controls */}
          <aside className="p-5 md:p-6 border-t md:border-t-0 md:border-l border-zinc-100 space-y-4 overflow-y-auto">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5 px-0.5">ไฟล์ภาพ</label>
              <button type="button" onClick={pickFile} disabled={saving}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg ring-1 ring-zinc-200 hover:border-amber-300 hover:bg-zinc-50 text-[13px] text-zinc-700">
                <Upload size={13} /> {src ? 'เลือกภาพใหม่' : 'เลือกภาพ'}
              </button>
              <input ref={fileRef} type="file" accept={ACCEPT} onChange={onFileChange} className="hidden" />
              <p className="mt-1.5 text-[11px] text-zinc-500 leading-relaxed">
                ภาพจะถูกตัดเป็นสี่เหลี่ยมจัตุรัสและย่อให้พอดี 500×500 px อัตโนมัติ
              </p>
            </div>

            {/* Background removal — opt-in (~30 MB model downloaded on first click,
                cached after). Original preserved so the user can revert if the
                result has artifacts. */}
            {src && (
              <div className="pt-3 border-t border-zinc-200/80">
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5 px-0.5">พื้นหลัง</label>
                {!bgRemoved ? (
                  <button type="button" onClick={removeBg} disabled={bgRemoving || saving}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white text-[13px] font-medium disabled:opacity-60">
                    {bgRemoving ? <><Loader2 size={13} className="animate-spin" /> กำลังประมวลผล…</> : <><Sparkles size={13} /> ลบพื้นหลังอัตโนมัติ</>}
                  </button>
                ) : (
                  <button type="button" onClick={revertBg} disabled={saving}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg ring-1 ring-zinc-200 hover:bg-zinc-50 text-[13px] text-zinc-700">
                    <Undo2 size={13} /> ใช้ภาพต้นฉบับ
                  </button>
                )}
                {bgErr ? (
                  <p className="mt-1.5 text-[11px] text-rose-600">ลบพื้นหลังไม่สำเร็จ: {bgErr}</p>
                ) : (
                  <p className="mt-1.5 text-[11px] text-zinc-500 leading-snug">
                    {bgRemoved
                      ? 'ใช้ภาพที่ลบพื้นหลังแล้ว — ถ้าผลไม่ดี กดปุ่มเพื่อกลับไปใช้ภาพต้นฉบับ'
                      : 'ทำงานในเครื่อง · ครั้งแรกอาจรอ ~30 MB · 1–3 วินาทีต่อภาพ'}
                  </p>
                )}
              </div>
            )}

            {src && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-1.5 px-0.5">
                    <span className="text-[12px] font-semibold text-zinc-700">ซูม</span>
                    <span className="text-[11px] text-zinc-500 tabular-nums">{zoom.toFixed(2)}×</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - 0.1))}
                      className="w-8 h-8 rounded-md ring-1 ring-zinc-200 hover:bg-zinc-50 inline-flex items-center justify-center text-zinc-600">
                      <ZoomOut size={14} />
                    </button>
                    <input type="range" min={ZOOM_MIN} max={ZOOM_MAX} step={0.01} value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="flex-1 accent-amber-500" />
                    <button type="button" onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + 0.25))}
                      className="w-8 h-8 rounded-md ring-1 ring-zinc-200 hover:bg-zinc-50 inline-flex items-center justify-center text-zinc-600">
                      <ZoomIn size={14} />
                    </button>
                  </div>
                  <p className="mt-1 text-[10.5px] text-zinc-400 leading-snug">
                    ซูมต่ำกว่า 1× เพื่อให้มีพื้นที่ว่างรอบโลโก้ — พื้นที่ว่างจะโปร่งใสในไฟล์ที่บันทึก
                  </p>
                </div>

                <button type="button" onClick={() => { setCrop({ x: 0, y: 0 }); setZoom(1) }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 ring-1 ring-zinc-200">
                  <RotateCcw size={12} /> รีเซ็ตการครอป
                </button>

                <div className="pt-3 border-t border-zinc-200/80">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">พรีวิว</p>
                  <SaveSizePreview src={src} croppedArea={croppedArea} />
                </div>
              </>
            )}

            {err && (
              <div className="px-3 py-2 rounded-lg bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12.5px] flex items-center gap-2">
                <AlertTriangle size={13} /> {err}
              </div>
            )}
          </aside>
        </div>

        <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3 shrink-0">
          <div className="text-[11.5px] text-zinc-400">
            {src ? 'ลากเพื่อย้าย · ใช้สโครลหรือ slider เพื่อซูม' : ' '}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={saving} className="px-3 py-1.5 rounded-md text-[13px] text-zinc-600 hover:bg-zinc-100">ยกเลิก</button>
            <button onClick={save} disabled={saving || !src || !croppedArea}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-medium disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} บันทึกโลโก้
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}


/* ──────────────────────────────── helpers ──────────────────────────────── */

/**
 * Render the user's cropped region into a square `size`×`size` canvas and
 * return a PNG data URI. The output is ALWAYS exactly `size`×`size` regardless
 * of source resolution — bigger sources are downsampled, smaller ones upscaled
 * (rare, since logos are usually 1k+).
 */
async function renderCropped(src, area, size) {
  const img = await loadImage(src)
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(
    img,
    area.x, area.y, area.width, area.height,
    0, 0, size, size,
  )
  return canvas.toDataURL('image/png')
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload  = () => resolve(img)
    img.onerror = () => reject(new Error('load image failed'))
    img.src = src
  })
}


/** Small live thumbnail of what will be saved. Re-renders whenever the crop
 *  region changes. */
function SaveSizePreview({ src, croppedArea }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    if (!src || !croppedArea || !canvasRef.current) return
    const c = canvasRef.current
    c.width = c.height = 96
    loadImage(src).then((img) => {
      if (cancelled) return
      const ctx = c.getContext('2d')
      ctx.clearRect(0, 0, c.width, c.height)
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img,
        croppedArea.x, croppedArea.y, croppedArea.width, croppedArea.height,
        0, 0, c.width, c.height)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [src, croppedArea])

  return (
    <div className="flex items-center gap-3">
      <div
        className="rounded-lg ring-1 ring-zinc-200 overflow-hidden"
        style={{
          width: 96, height: 96,
          backgroundColor: '#f5f5f4',
          backgroundImage: 'linear-gradient(45deg, #e5e5e4 25%, transparent 25%), linear-gradient(-45deg, #e5e5e4 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e5e4 75%), linear-gradient(-45deg, transparent 75%, #e5e5e4 75%)',
          backgroundSize: '12px 12px',
          backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
        }}
      >
        <canvas ref={canvasRef} className="block w-full h-full" />
      </div>
      <div className="text-[11.5px] text-zinc-500 leading-relaxed">
        บันทึกที่ <strong className="text-zinc-700">500×500 px</strong><br />
        PNG (พื้นที่ว่างจะโปร่งใส)
      </div>
    </div>
  )
}
